// state/routeReducer.js
import { ROUTE_CONFIG } from '../config/routeConfig';

export const initialState = {
  // Core data
  loading: false,
  selectedDate: new Date(),
  crews: [],
  sites: [],
  weeklyRoutes: [],
  weeklyHours: {},

  // Filtered/derived data
  unassignedJobs: [],
  filteredMarkers: [],
  activeRouteDetails: {},
  // jobSiteDetails: {},
  jobSiteDetails: null, 
  crewLocations: [],
  
  // UI state
  ui: {
    isUnassignmentModalOpen: false,
    zoomLevel: ROUTE_CONFIG.MAP.DEFAULT_ZOOM,
    center: ROUTE_CONFIG.MAP.DEFAULT_CENTER,
    highlightedSite: '',
    highlightedNearbySite: '',
    tabValue: 0,
  },
  
  // Filters
  filters: {
    selectedCrew: null,
    activeMarker: null,
    highlightedStop: null,
    searchTerm: '',
    sortMethod: 'name',
    sortDirection: 'asc'
  },
  
  // Cache for expensive operations
  cache: {
    routes: {}, // keyed by date-crewId
    siteDetails: {},
    lastFetchDate: null,
    weekStart: null,
    weekEnd: null,
    futureVisits: []
  }
};

export const routeReducer = (state, action) => {
  // console.log('routeReducer...', action)
  switch (action.type) {
    // Loading states
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    // Core data updates
    case 'SET_ROUTES':
      console.log('📄 SET_ROUTES action:', {
        routesCount: action.payload.routes?.length,
        sitesCount: action.payload.sites?.length,
        routes: action.payload.routes
      });
      return {
        ...state,
        weeklyRoutes: action.payload.routes,
        sites: action.payload.sites || state.sites,
        cache: {
          ...state.cache,
          weekStart: action.payload.weekStart,
          weekEnd: action.payload.weekEnd,
          futureVisits: action.payload.futureVisits || state.cache.futureVisits
        },
        loading: false
      };
    
    case 'SET_CREWS':
      return { ...state, crews: action.payload };
    
    case 'SET_WEEKLY_HOURS':
      console.log('📋 SET_WEEKLY_HOURS action:', action.payload);
      return { ...state, weeklyHours: action.payload };
    
    case 'SET_UNASSIGNED_JOBS':
      return { ...state, unassignedJobs: action.payload };
    
    case 'SET_FILTERED_MARKERS':
      return { ...state, filteredMarkers: action.payload };
    
    // Selection/filter updates
    case 'SELECT_CREW':
      return {
        ...state,
        filters: { ...state.filters, selectedCrew: action.payload }
      };
    
    case 'SET_ACTIVE_MARKER':
      return {
        ...state,
        filters: { ...state.filters, activeMarker: action.payload }
      };
    case 'SET_HIGHLIGHTED_STOP':
      return {
        ...state,
        filters: { ...state.filters, highlightedStop: action.payload }
      };
    case 'UPDATE_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload }
      };
    
    // UI updates
    case 'UPDATE_UI':
      let newState = {
        ...state
      }
      if (action.payload?.selectedDate) newState.selectedDate = action.payload.selectedDate
      return {
        ...newState,
        ui: { ...state.ui, ...action.payload },
        loading: false
      };
    
    // Complex updates
    case 'ASSIGN_JOB_SUCCESS':
      // Optimistically add future visits if they were created
      let newFutureVisits = [...state.cache.futureVisits];
      if (action.payload.scheduledDates) {
        // Add the new future visits to the cache
        Object.entries(action.payload.scheduledDates).forEach(([key, dateString]) => {
          newFutureVisits.push({
            siteId: action.payload.siteId,
            crew: action.payload.crewId,
            visitDate: dateString,
            frequency: action.payload.frequency || 'Future Visit',
            quarterKey: key
          });
        });
      }
      
      return {
        ...state,
        unassignedJobs: state.unassignedJobs.filter(job => job._id !== action.payload.siteId),
        filters: { 
          ...state.filters, 
          activeMarker: null,
          highlightedStop: null // NEW - clear highlighted stop too
        },
        ui: { ...state.ui, highlightedSite: '', highlightedNearbySite: '' },
        cache: {
          ...state.cache,
          futureVisits: newFutureVisits
        }
      };
    
    case 'UNASSIGN_JOB_SUCCESS':
      return {
        ...state,
        unassignedJobs: [...state.unassignedJobs, action.payload.site],
        filters: { ...state.filters, activeMarker: null }
      };
    
    // Batch updates
    case 'RESET_FILTERS':
      return {
        ...state,
        filters: {
          ...initialState.filters,
          searchTerm: state.filters.searchTerm // Preserve search
        },
        ui: {
          ...state.ui,
          highlightedSite: '',
          highlightedNearbySite: ''
        }
      };
    
    case 'UPDATE_JOB_DETAILS':
      console.log('UPDATE_JOB_DETAILS...', action)
      return {
        ...state,
        jobSiteDetails: action.payload,
        activeRouteDetails: action.payload.routeDetails || state.activeRouteDetails
      };

    case 'CACHE_ROUTE_DATA':
      return {
        ...state,
        cache: {
          ...state.cache,
          routes: {
            ...state.cache.routes,
            [action.payload.key]: {
              data: action.payload.data,
              timestamp: Date.now()
            }
          }
        }
      };
    
    case 'CACHE_SITE_DETAILS':
      return {
        ...state,
        cache: {
          ...state.cache,
          siteDetails: {
            ...state.cache.siteDetails,
            [action.payload.siteId]: {
              data: action.payload.data,
              timestamp: Date.now()
            }
          }
        }
      };
    
    case 'CLEAR_EXPIRED_CACHE':
      const now = Date.now();
      const routeTTL = ROUTE_CONFIG.CACHE.ROUTE_TTL;
      const siteTTL = ROUTE_CONFIG.CACHE.SITE_DETAILS_TTL;
      
      return {
        ...state,
        cache: {
          ...state.cache,
          routes: Object.entries(state.cache.routes).reduce((acc, [key, value]) => {
            if (now - value.timestamp < routeTTL) {
              acc[key] = value;
            }
            return acc;
          }, {}),
          siteDetails: Object.entries(state.cache.siteDetails).reduce((acc, [key, value]) => {
            if (now - value.timestamp < siteTTL) {
              acc[key] = value;
            }
            return acc;
          }, {})
        }
      };  
    case 'CLEAR_ROUTE_CACHE':
      return {
        ...state,
        cache: {
          ...state.cache,
          routes: {}
        }
      };
    
    case 'CLEAR_SITE_CACHE':
      const { [action.payload.siteId]: removed, ...remainingSiteDetails } = state.cache.siteDetails;
      return {
        ...state,
        cache: {
          ...state.cache,
          siteDetails: remainingSiteDetails
        }
      };  

    default:
      return state;
  }
};
